import { useEffect, useMemo, useState } from 'react';
import { api, fetchBacklog } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { useAppData } from '../AppData';
import { BacklogTask, Review, REVIEW_VALORACIONES, VALORACION_LABELS } from '../domain/types';
import { fmtDate, fmtDia, nameByEmail, errMsg, taskHeading } from '../domain/util';
import { SIN_PROYECTO, agruparRevisiones, proyectosConPendientes } from '../domain/revisiones';

interface Draft {
  valoracion: string;
  descripcion: string;
}

export default function Revisiones() {
  const { email, role } = useAuth();
  const { users, reload } = useAppData();
  const [pending, setPending] = useState<Review[]>([]);
  const [tasks, setTasks] = useState<Record<string, BacklogTask>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [projFilter, setProjFilter] = useState('');

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const [revs, backlog] = await Promise.all([
        api.listReviews({ status: 'pending' }),
        fetchBacklog(role || 'user', email),
      ]);
      const byId: Record<string, BacklogTask> = {};
      for (const t of backlog) byId[t.id] = t;
      setTasks(byId);
      setPending(revs);
    } catch (e) {
      setErr(errMsg(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const draftFor = (id: string): Draft => drafts[id] || { valoracion: '', descripcion: '' };
  const setDraft = (id: string, patch: Partial<Draft>) =>
    setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), ...patch } }));

  const groups = useMemo(
    () => agruparRevisiones(pending, tasks, projFilter),
    [pending, tasks, projFilter]
  );
  const opcionesProyecto = useMemo(() => proyectosConPendientes(pending, tasks), [pending, tasks]);

  // El contador acompaña lo que se está viendo, como el del Backlog. Con el
  // filtro puesto se aclara sobre cuántas del total está recortando.
  const visiblePending = useMemo(() => groups.reduce((n, g) => n + g.revs.length, 0), [groups]);
  const totalPending = useMemo(
    () => pending.filter((r) => tasks[r.task_id]?.status === 'done').length,
    [pending, tasks]
  );

  const submit = async (r: Review) => {
    const d = draftFor(r.task_id);
    setErr(''); setMsg('');
    if (!d.valoracion) return setErr('Elegí una valoración (aprobado o rechazado).');
    if (!d.descripcion.trim()) return setErr('La descripción de la revisión es obligatoria.');
    setBusy(r.task_id);
    try {
      await api.submitReview({ task_id: r.task_id, valoracion: d.valoracion, descripcion: d.descripcion.trim() });
      setMsg('Revisión cargada. La tarea quedó cerrada.');
      setDrafts((prev) => { const n = { ...prev }; delete n[r.task_id]; return n; });
      await load();
      await reload(); // refresca el mapa de revisiones global (lock del modal, dashboards)
    } catch (e) {
      setErr(errMsg(e));
    }
    setBusy('');
  };

  return (
    <div className="grid">
      <div className="panel page-hero">
        <div className="eyebrow">Ciclo de revisión</div>
        <h1>Revisiones de cliente</h1>
        <p className="muted">
          Tareas finalizadas esperando la valoración del cliente. Al cargar la revisión, la tarea
          queda cerrada y no vuelve a cambiar de estado.
        </p>
        <div className="stat-strip">
          <div className="stat-card">
            <span className="stat-value">{visiblePending}</span>
            <span className="stat-label">
              {visiblePending === 1 ? 'pendiente' : 'pendientes'}
              {projFilter && visiblePending !== totalPending && ` de ${totalPending}`}
            </span>
          </div>
        </div>
      </div>

      {!loading && totalPending > 0 && (
      <div className="panel row filters-grid">
        <div style={{ flex: 1, minWidth: 220 }}>
          <label>Proyecto</label>
          <select value={projFilter} onChange={(e) => setProjFilter(e.target.value)}>
            <option value="">Todos los proyectos</option>
            {opcionesProyecto.haySinProyecto && <option value={SIN_PROYECTO}>Sin proyecto</option>}
            {opcionesProyecto.nombres.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {projFilter && (
          <div className="filter-actions">
            <button className="ghost sm" type="button" onClick={() => setProjFilter('')}>Limpiar filtro</button>
          </div>
        )}
      </div>
      )}

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {loading ? (
        <div className="panel"><p className="muted">Cargando…</p></div>
      ) : groups.length === 0 ? (
        <div className="panel">
          <p className="muted">
            {totalPending > 0
              ? 'Ninguna tarea pendiente de revisión coincide con el filtro.'
              : 'No hay tareas pendientes de revisión.'}
          </p>
        </div>
      ) : (
        groups.map((g) => (
          <div className="panel-flush proj-group" key={g.name || '__none'}>
            <div className="proj-head">
              <span className="proj-name">{g.name || 'Sin proyecto'}</span>
              <span className="spacer" />
              <span className="proj-count">{g.revs.length} {g.revs.length === 1 ? 'tarea' : 'tareas'}</span>
            </div>
            <div className="grid" style={{ padding: 12 }}>
              {g.revs.map((r) => {
                const t = tasks[r.task_id];
                const d = draftFor(r.task_id);
                return (
                  <div className="panel" key={r.task_id}>
                    <div className="task-title" style={{ fontWeight: 600 }}>{taskHeading(t.titulo, t.description, 96)}</div>
                    <div className="ficha" style={{ marginTop: 8 }}>
                      <div><div className="k">Espacio / Cliente</div><div className="v">{t.espacio || t.cliente_interno || '—'}</div></div>
                      <div><div className="k">Responsable</div><div className="v">{nameByEmail(t.assigned_to, users)}</div></div>
                      <div><div className="k">Entrega</div><div className="v">{fmtDia(t.deadline)}</div></div>
                      <div><div className="k">Finalizada</div><div className="v">{fmtDate(t.finished_at)}</div></div>
                    </div>
                    <div className="grid" style={{ gridTemplateColumns: '200px 1fr', marginTop: 12, alignItems: 'end' }}>
                      <div>
                        <label>Valoración</label>
                        <select value={d.valoracion} onChange={(e) => setDraft(r.task_id, { valoracion: e.target.value })}>
                          <option value="">—</option>
                          {REVIEW_VALORACIONES.map((v) => <option key={v} value={v}>{VALORACION_LABELS[v]}</option>)}
                        </select>
                      </div>
                      <div>
                        <label>Descripción de la revisión</label>
                        <textarea rows={2} value={d.descripcion} onChange={(e) => setDraft(r.task_id, { descripcion: e.target.value })} placeholder="Comentario del cliente sobre la entrega…" />
                      </div>
                    </div>
                    <div className="row" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
                      <button className="primary" disabled={busy === r.task_id} onClick={() => submit(r)}>
                        {busy === r.task_id ? 'Guardando…' : 'Guardar revisión'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
